import {
  computed,
  effect,
  EnvironmentProviders,
  inject,
  Injectable,
  InjectionToken,
  Injector,
  provideAppInitializer,
  signal,
  untracked,
} from '@angular/core';
import { QueryClient } from '@tanstack/angular-query-experimental';

import { SessionService } from '@movilidad/app-core';
import type { PusherService } from '@movilidad/app-core/pusher';
import { balanceKeys } from '@shared/stores/balance.keys';

/** Evento de `App\Events\UserBusticketsChanged` (`broadcastAs`). */
export const BUSTICKETS_CHANGED_EVENT = 'bustickets.changed';

/** Lo que trae el aviso: sólo ids y la acción. Es una pista; los datos se vuelven a pedir al API. */
export interface BusticketsChanged {
  action: string;
  busticket_ids: number[];
}

/**
 * Carga PusherService bajo demanda: `@movilidad/app-core/pusher` (pusher-js, ~60 kB) queda fuera
 * del bundle inicial aunque el canal se abra al arrancar. Token propio para que las pruebas lo cambien.
 */
export const PUSHER_LOADER = new InjectionToken<() => Promise<PusherService>>('PUSHER_LOADER', {
  providedIn: 'root',
  factory: () => {
    const injector = inject(Injector);
    return async () => injector.get((await import('./pusher-lazy')).PusherService);
  },
});

/**
 * TEC-464: "tus boletos cambiaron" en vivo. Con sesión escucha `private-user.{id}` (lo autoriza
 * `App\Broadcasting\UserChannel` sólo para ese usuario) y, con cada aviso del API (escaneo del
 * conductor, cancelación desde el panel, No Show, canje, compra…), invalida las consultas de
 * boletos de TanStack y sube `changes()` para las pantallas que no usan TanStack (Mis boletos).
 * Sale del canal al cerrar sesión o al cambiar de usuario. Los builds viejos no lo escuchan: el
 * API publica igual y nada cambia para ellos.
 */
@Injectable({ providedIn: 'root' })
export class BusticketLiveService {
  readonly #session = inject(SessionService);
  readonly #queryClient = inject(QueryClient);
  readonly #loadPusher = inject(PUSHER_LOADER);

  readonly #changes = signal(0);
  /** Sube con cada aviso (y al reconectar, por si se perdió alguno). */
  readonly changes = this.#changes.asReadonly();

  // Sólo el id: refrescar `me` (mismo usuario) no debe re-suscribir.
  readonly #userId = computed(() => this.#session.user()?.id ?? null);
  #current: { name: string; pusher: PusherService } | null = null;
  #generation = 0;

  constructor() {
    effect(() => {
      const userId = this.#userId();
      untracked(() => void this.#follow(userId));
    });
  }

  async #follow(userId: number | string | null): Promise<void> {
    const generation = ++this.#generation;
    this.#leave();
    if (userId === null || userId === undefined || userId === '') {
      return;
    }

    let pusher: PusherService;
    try {
      pusher = await this.#loadPusher();
    } catch (error) {
      console.warn('[bustickets-live] sin tiempo real:', error);
      return;
    }
    // Cerró sesión (o entró otro usuario) mientras cargaba pusher-js.
    if (generation !== this.#generation) {
      return;
    }

    const name = `user.${userId}`;
    const channel = pusher.privateChannel(name);
    let subscribedOnce = false;
    channel.bind('pusher:subscription_succeeded', () => {
      // pusher-js re-suscribe al reconectar: lo que pasó sin conexión no llegó, se vuelve a pedir.
      if (subscribedOnce) {
        this.#notify();
      }
      subscribedOnce = true;
    });
    channel.bind(BUSTICKETS_CHANGED_EVENT, () => this.#notify());
    this.#current = { name, pusher };
  }

  #leave(): void {
    if (this.#current) {
      this.#current.pusher.leavePrivateChannel(this.#current.name);
      this.#current = null;
    }
  }

  #notify(): void {
    // Detalle del boleto y saldo (una cancelación reembolsa, el cargo por bajada cobra).
    void this.#queryClient.invalidateQueries({ queryKey: ['busticket'] });
    void this.#queryClient.invalidateQueries({ queryKey: balanceKeys.all });
    this.#changes.update((n) => n + 1);
  }
}

/** Arranca BusticketLiveService con el app (va en los providers de `main.ts`). */
export function provideBusticketLive(): EnvironmentProviders {
  return provideAppInitializer(() => {
    inject(BusticketLiveService);
  });
}
